import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { CameraOutlined, QrcodeOutlined } from '@ant-design/icons'
import { Alert, Button, Divider, Flex, Input, Segmented, type InputRef } from 'antd'
import { useTranslation } from 'react-i18next'
import { decodeImageFile, preloadQrDecoder } from '@/features/portal/qrDecode'
import { CARD_DIGITS_MAX, cardDigits, cardFromQrText, type VoucherCard } from '@/features/portal/voucherCard'
import { QrCamera, type CameraFailure } from './QrCamera'

export type ScanMethod = 'QR_SCAN' | 'QR_IMAGE'
export type TypedEntry = 'CODE' | 'LOGIN'

export interface LoginEntry {
  mode: TypedEntry
  onModeChange: (mode: TypedEntry) => void
  login: string
  password: string
  onLoginChange: (login: string) => void
  onPasswordChange: (password: string) => void
}

interface CardEntryProps {
  code: string
  onCodeChange: (code: string) => void
  onCodeSubmit?: () => void
  onCard: (card: VoucherCard, method: ScanMethod) => void
  loginEntry?: LoginEntry
  disabled: boolean
}

export function CardEntry({ code, onCodeChange, onCodeSubmit, onCard, loginEntry, disabled }: CardEntryProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<InputRef>(null)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [readingPhoto, setReadingPhoto] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(preloadQrDecoder, [])

  const openCamera = (): void => {
    setNotice(null)
    setCameraOpen(true)
  }

  const acceptScanned = (text: string): boolean => {
    const card = cardFromQrText(text)
    if (!card) {
      setNotice(t('card.notCard'))
      return false
    }
    setCameraOpen(false)
    setNotice(null)
    onCard(card, 'QR_SCAN')
    return true
  }

  const cameraFailed = (reason: CameraFailure): void => {
    setCameraOpen(false)
    setNotice(t(`card.camera.${reason}`))
  }

  const readPhoto = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setNotice(null)
    setReadingPhoto(true)
    const text = await decodeImageFile(file).catch(() => null)
    setReadingPhoto(false)
    const card = text === null ? null : cardFromQrText(text)
    if (card) onCard(card, 'QR_IMAGE')
    else setNotice(text === null ? t('card.noQrInPhoto') : t('card.notCard'))
  }

  const busy = disabled || readingPhoto

  return (
    <Flex vertical gap={10}>
      {notice ? <Alert type="warning" showIcon message={notice} /> : null}

      {cameraOpen ? (
        <>
          <QrCamera onText={acceptScanned} onFailure={cameraFailed} />
          <Button block onClick={() => setCameraOpen(false)}>
            {t('common.cancel')}
          </Button>
        </>
      ) : (
        <>
          <Button type="primary" size="large" block icon={<QrcodeOutlined />} onClick={openCamera} disabled={busy}>
            {t('card.scan')}
          </Button>
          <Button
            size="large"
            block
            icon={<CameraOutlined />}
            loading={readingPhoto}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            {readingPhoto ? t('card.reading') : t('card.upload')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(event) => void readPhoto(event)}
          />
          <Divider plain style={{ margin: '4px 0' }}>
            {loginEntry?.mode === 'LOGIN' ? t('card.orTypeLogin') : t('card.orType')}
          </Divider>
          {loginEntry ? (
            <Segmented<TypedEntry>
              block
              value={loginEntry.mode}
              onChange={loginEntry.onModeChange}
              disabled={busy}
              options={[
                { value: 'CODE', label: t('card.modeCode') },
                { value: 'LOGIN', label: t('card.modeLogin') },
              ]}
            />
          ) : null}
          {loginEntry?.mode === 'LOGIN' ? (
            <>
              <Input
                size="large"
                value={loginEntry.login}
                onChange={(event) => loginEntry.onLoginChange(cardDigits(event.target.value))}
                placeholder={t('voucher.loginLabel')}
                aria-label={t('voucher.loginLabel')}
                onPressEnter={() => passwordRef.current?.focus()}
                disabled={busy}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={CARD_DIGITS_MAX}
                autoComplete="off"
                spellCheck={false}
                dir="ltr"
              />
              <Input.Password
                ref={passwordRef}
                size="large"
                value={loginEntry.password}
                onChange={(event) => loginEntry.onPasswordChange(cardDigits(event.target.value))}
                placeholder={t('voucher.passwordLabel')}
                aria-label={t('voucher.passwordLabel')}
                onPressEnter={onCodeSubmit}
                disabled={busy}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={CARD_DIGITS_MAX}
                autoComplete="off"
                spellCheck={false}
                dir="ltr"
              />
            </>
          ) : (
            <Input
              size="large"
              value={code}
              onChange={(event) => onCodeChange(event.target.value.toUpperCase())}
              placeholder={t('voucher.codeLabel')}
              aria-label={t('voucher.codeLabel')}
              onPressEnter={onCodeSubmit}
              disabled={busy}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              dir="ltr"
            />
          )}
        </>
      )}
    </Flex>
  )
}
